import * as React from "react"
import { ChevronDown } from "lucide-react"

import { cn } from "@/lib/utils"

export interface SelectProps
  extends React.SelectHTMLAttributes<HTMLSelectElement> {
  /** Extra classes for the wrapper element (e.g. `w-full` to fill its column). */
  containerClassName?: string
}

/**
 * Native `<select>` styled to match `Input`, with a custom chevron that always
 * keeps proper space from the right edge.
 *
 * Use this instead of a raw `<select>` so dropdown arrows never crowd the
 * border. Horizontal padding (`pl-3 pr-9`) and the chevron are owned by this
 * component on purpose — do NOT pass `px-*`/`pr-*` via className, or you'll
 * reintroduce the cramped-arrow bug (twMerge would override the reserved gap).
 * Control height / text size / width / background via className
 * (e.g. `h-8 text-xs`) and wrapper width via `containerClassName`.
 */
const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  ({ className, containerClassName, children, ...props }, ref) => {
    return (
      <div className={cn("relative inline-flex", containerClassName)}>
        <select
          ref={ref}
          className={cn(
            "h-10 w-full appearance-none rounded-md border border-input bg-background pl-3 pr-9 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
            className
          )}
          {...props}
        >
          {children}
        </select>
        <ChevronDown
          className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden="true"
        />
      </div>
    )
  }
)
Select.displayName = "Select"

export { Select }
